import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { InfiniteDebouncedAutocomplete } from 'components';

import useNotifications from 'hooks/common/useNotifications';

import { searchAccounts } from 'services/accounts.service';
import extractAccountName from './utils/extractAccountName';

import { DefaultPagination } from 'constants/pagination';

import { UserAccountSearchType } from 'types/user';
import { IUserAccountsAutocompleteProps } from './UserAccountsAutocomplete.types';
import { IInfiniteDebounecedAutocompleteSearchParams } from 'components/inputs/InfiniteDebouncedAutocomplete';
import { IdType } from 'types/common';

const PAGE_SIZE = 20;

const UserAccountsAutocomplete = ({
  uid,
  value,
  placeholder,
  fullAccount,
  onChange,
  onSelect,
  onClear,
  ...props
}: IUserAccountsAutocompleteProps) => {
  const accountName = extractAccountName(value);

  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(accountName);
  const [options, setOptions] = useState([]);

  const { t } = useTranslation();
  const { notificationApi } = useNotifications();

  const generateOption = (account: UserAccountSearchType) => {
    return {
      value: JSON.stringify(account),
      label: account.account_name,
    };
  };

  const getUsers = useCallback(
    async ({ search, page, pageSize }: IInfiniteDebounecedAutocompleteSearchParams) => {
      const { data, success, errorMessage } = await searchAccounts({ uid, search, page, per_page: pageSize });

      if (success) {
        const accounts = data.data;
        const newOptions = accounts.map(account => generateOption(account));
        const isFirstPage = page === DefaultPagination.START_PAGE;

        setTotal(data?.context?.total_rows || 0);
        setOptions(prevOptions => (isFirstPage ? newOptions : [...prevOptions, ...newOptions]));
      } else {
        notificationApi.error({ title: t(errorMessage) });
      }
    },
    [notificationApi, t, uid],
  );

  const handleSelect = async (value: string) => {
    const account: UserAccountSearchType = JSON.parse(value);

    const aid: IdType = account.account_id || '';
    const accountName: string = account.account_name || '';

    const newValue = fullAccount ? account : aid;

    onChange && onChange(newValue);
    onSelect && onSelect(newValue);
    setSearch(accountName);
  };

  const handleChange = async (value: string) => {
    setSearch(value);
  };

  const handleClear = async () => {
    if (value) {
      setSearch('');
      onChange && onChange(null);
      onClear && onClear();
    }
  };

  useEffect(() => {
    setSearch(accountName);
  }, [accountName]);

  return (
    <InfiniteDebouncedAutocomplete
      popupMatchSelectWidth={false}
      total={total}
      value={search}
      options={options}
      pageSize={PAGE_SIZE}
      placeholder={t(placeholder)}
      onSearch={getUsers}
      onSelect={handleSelect}
      onChange={handleChange}
      onClear={handleClear}
      {...props}
    />
  );
};

export default UserAccountsAutocomplete;
